import { useAuthentication } from "@aistihealth/web-common/src/msal/clientMsal"
import React, { FC, useContext, useEffect, useState } from "react"
import { createApolloContext } from "../api/gql/client"
import {
  GetAuthorizationForResourceTypesQuery,
  GetMeQuery,
  useGetAuthorizationForResourceTypesLazyQuery,
  useGetMeLazyQuery,
} from "../api/gql/admin/generated/graphql"

const enableNewAdminAuthorizationModel =
  process.env.NEXT_PUBLIC_FEATURE_ENABLE_NEW_ADMIN_AUTHORIZATION_MODEL ===
  "true"

export interface AdminAuthzContextValue {
  adminUser?: GetMeQuery["me"]
  authorizedResources?: GetAuthorizationForResourceTypesQuery["authorizeResourceTypes"]
  loading: boolean
}

// Used for old authz and non-admin pages
const defaultValue: AdminAuthzContextValue = {
  // TODO AIS-1046 Improve global role listing
  adminUser: { globalRoles: ["superadmin"] },
  loading: false,
}

const AdminAuthzContext = React.createContext<AdminAuthzContextValue>(
  defaultValue,
)

/**
 * Provide the authz context.
 *
 * If the provider is not present, the default value pretends that the user is a superadmin to retain
 * compatibility with non-admin pages and old authz.
 */
export const AdminAuthzProvider: FC = ({ children }) => {
  const auth = useAuthentication()

  const [contextValue, setContextValue] = useState<AdminAuthzContextValue>({
    adminUser: undefined,
    loading: true,
  })

  const [fetchMe] = useGetMeLazyQuery()
  const [
    authorizeResourceTypes,
    { error: authzError },
  ] = useGetAuthorizationForResourceTypesLazyQuery()

  useEffect(() => {
    if (!enableNewAdminAuthorizationModel) {
      return
    }

    if (!auth) {
      setContextValue({
        adminUser: undefined,
        loading: true,
      })

      return
    }

    const execute = async () => {
      const meResult = await fetchMe({
        ...createApolloContext(auth),
      })

      const resourceTypesResult = await authorizeResourceTypes({
        ...createApolloContext(auth),
        variables: {
          input: [
            {
              queryId: "questionListAccess",
              action: "read_question_list",
              resourceType: "OsoQuestionList",
            },
            {
              queryId: "trialUsersAccess",
              action: "read_trial_users",
              resourceType: "OsoReport",
            },
            {
              queryId: "allResultsAccess",
              action: "read_all_results",
              resourceType: "OsoReport",
            },
            {
              queryId: "assessmentOrderAccess",
              action: "read",
              resourceType: "OsoAssessmentOrder",
            },
            {
              queryId: "companyGroupAccess",
              action: "read",
              resourceType: "OsoCompanyGroup",
            },
            {
              queryId: "userAccess",
              action: "read",
              resourceType: "OsoUser",
            },
            {
              queryId: "adminUserAccess",
              action: "read",
              resourceType: "OsoAdminUser",
            },
            {
              queryId: "adminGroupAccess",
              action: "read",
              resourceType: "OsoAdminGroup",
            },
          ],
        },
      })

      setContextValue({
        adminUser: meResult.data.me,
        authorizedResources: resourceTypesResult.data.authorizeResourceTypes,
        loading: false,
      })
    }

    execute()
  }, [auth, authorizeResourceTypes, fetchMe])

  if (authzError) {
    // TODO #AIS-511 Add proper error handling
    // eslint-disable-next-line no-alert
    alert(`Could not fetch data: ${authzError.message}`)
  }

  return (
    <AdminAuthzContext.Provider value={contextValue}>
      {children}
    </AdminAuthzContext.Provider>
  )
}

export const useAdminAuthz = (): AdminAuthzContextValue =>
  useContext(AdminAuthzContext)
